import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLogo from './AppLogo';
import AppFooter from './AppFooter';
import { getCommunities } from '../services/communitiesService';
import { ONBOARDING_COUNTRIES, ONBOARDING_UI } from '../config/onboardingConfig';

const RegisterScreen = ({ onRegister, onShowLogin, message }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [country, setCountry] = useState('');
  const [communityId, setCommunityId] = useState('');
  const [communities, setCommunities] = useState([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);
  const [communitiesError, setCommunitiesError] = useState('');

  useEffect(() => {
    if (!country) {
      setCommunities([]);
      setCommunityId('');
      setCommunitiesError('');
      return;
    }

    let cancelled = false;
    setCommunitiesLoading(true);
    setCommunitiesError('');
    setCommunityId('');

    getCommunities(country)
      .then((rows) => {
        if (!cancelled) {
          setCommunities(rows);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setCommunities([]);
          setCommunitiesError(e?.message || 'Could not load communities');
        }
      })
      .finally(() => {
        if (!cancelled) setCommunitiesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [country]);

  const canSubmit =
    email &&
    password &&
    country &&
    communityId &&
    termsAccepted &&
    !communitiesLoading &&
    !communitiesError;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <AppLogo className="h-10 w-10 object-contain" />
          </div>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-800">SustainabilityHub</h1>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200/70">
              Pilot
            </span>
          </div>
          <p className="text-gray-600 mt-2">Tiers for verified climate actions (last 7 days)</p>
        </div>

        {message && <div className="text-red-500 text-sm mb-2">{message}</div>}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter your email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter your password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
            >
              <option value="">Select your country</option>
              {ONBOARDING_COUNTRIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Community</label>
            {!country ? (
              <p className="text-sm text-gray-500">Select a country first.</p>
            ) : communitiesLoading ? (
              <p className="text-sm text-gray-500">Loading communities…</p>
            ) : communitiesError ? (
              <p className="text-sm text-red-600">{communitiesError}</p>
            ) : communities.length === 0 ? (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                {ONBOARDING_UI.noCommunitiesForCountry}
              </p>
            ) : (
              <select
                value={communityId}
                onChange={(e) => setCommunityId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
              >
                <option value="">Select your community</option>
                {communities.map((com) => (
                  <option key={com.id} value={com.id}>
                    {com.name}
                    {com.communityType ? ` (${com.communityType})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500 shrink-0"
            />
            <span className="leading-snug">
              I agree to the{' '}
              <Link to="/terms" className="text-green-700 font-medium hover:underline underline-offset-2">
                Terms & Conditions
              </Link>
            </span>
          </label>

          <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 text-green-600 focus:ring-green-500 shrink-0"
            />
            <span className="leading-snug">
              I agree to receive updates, rewards, partner offers, and launch communications from SustainabilityHub. I
              can unsubscribe at any time.
            </span>
          </label>

          <div className="text-center mt-4">
            <button
              type="button"
              onClick={onShowLogin}
              className="text-green-700 hover:underline"
            >
              Already have an account?
            </button>
          </div>

          <button
            type="button"
            onClick={() => onRegister(email, password, country, communityId, marketingConsent)}
            disabled={!canSubmit}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Register
          </button>

          <AppFooter className="mt-6 pt-6" />
        </div>
      </div>
    </div>
  );
};

export default RegisterScreen;
